import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../types/users';
import aiConfigService from '../../services/aiConfigService';

const AISettings = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState({
    planningStrategy: 'multi-agents', // Stratégie de planification
    provider: 'proxy-llm',
    model: 'openai/gpt-4o-mini', // Meilleur modèle pour la planification (Déc 2024)
    apiKey: '',
    temperature: 0.1,
    maxTokens: 4000, // Plus élevé pour permettre un raisonnement approfondi
    enableMultipleSolutions: false,
    maxSolutions: 3
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  // Providers disponibles
  const providers = [
    { value: 'proxy-llm', label: 'Proxy LLM Orange (Entreprise)' },
    { value: 'openai', label: 'OpenAI Direct' },
    { value: 'anthropic', label: 'Anthropic Claude' }
  ];

  // Modèles disponibles selon le provider
  const getAvailableModels = (provider) => {
    switch (provider) {
      case 'proxy-llm':
        return [
          // 🌟 MODÈLES RECOMMANDÉS POUR LA PLANIFICATION (Décembre 2024)
          { value: 'vertex_ai/claude-opus-4-5', label: '🥇 Claude Opus 4.5 (Meilleur pour planification)' },
          { value: 'openai/gpt-5.1', label: '🥈 GPT-5.1 (Excellent - Dernier modèle)' },
          { value: 'vertex_ai/claude-sonnet-4-5', label: '🥉 Claude Sonnet 4.5 (Rapide et puissant)' },
          { value: 'openai/gpt-5.1-codex', label: '💻 GPT-5.1 Codex (Spécialisé code)' },
          
          // OpenAI Models
          { value: 'openai/gpt-4o-mini', label: 'GPT-4o Mini' },
          { value: 'openai/gpt-4o', label: 'GPT-4o' },
          { value: 'openai/gpt-4-turbo', label: 'GPT-4 Turbo' },
          { value: 'openai/gpt-4.1', label: 'GPT-4.1' },
          { value: 'openai/gpt-4.1-mini', label: 'GPT-4.1 Mini' },
          { value: 'openai/gpt-4.1-nano', label: 'GPT-4.1 Nano' },
          { value: 'openai/gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
          { value: 'openai/gpt-5', label: 'GPT-5' },
          { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini' },
          { value: 'openai/gpt-5-chat', label: 'GPT-5 Chat' },
          { value: 'openai/gpt-5-nano', label: 'GPT-5 Nano' },
          { value: 'openai/dall-e-3', label: 'DALL-E 3' },
          { value: 'openai/o1', label: 'OpenAI o1' },
          { value: 'openai/o1-mini', label: 'OpenAI o1 Mini' },
          { value: 'openai/o1-preview', label: 'OpenAI o1 Preview' },
          { value: 'openai/o3', label: 'OpenAI o3' },
          { value: 'openai/o3-mini', label: 'OpenAI o3 Mini' },
          { value: 'openai/o4-mini', label: 'OpenAI o4 Mini' },
          { value: 'openai/o4-mini-deep-research', label: 'OpenAI o4 Mini Deep Research' },
          { value: 'openai/whisper-1', label: 'Whisper-1 (Audio)' },
          { value: 'openai/text-embedding-3-small', label: 'Text Embedding 3 Small' },
          { value: 'openai/text-embedding-ada-002', label: 'Text Embedding Ada 002' },
          
          // Anthropic Claude Models
          { value: 'anthropic/claude-3-sonnet', label: 'Claude 3 Sonnet' },
          { value: 'anthropic/claude-3-haiku', label: 'Claude 3 Haiku' },
          { value: 'claude-3-5-haiku@20241022', label: 'Claude 3.5 Haiku' },
          { value: 'claude-sonnet-4@20250514', label: 'Claude Sonnet 4' },
          { value: 'claude-3-7-sonnet@20250219', label: 'Claude 3.7 Sonnet' },
          
          // Vertex AI Models
          { value: 'vertex_ai/claude3.5-sonnet-v2', label: 'Vertex AI Claude 3.5 Sonnet v2' },
          { value: 'vertex_ai/claude3.7-sonnet', label: 'Vertex AI Claude 3.7 Sonnet' },
          { value: 'vertex_ai/codestral', label: 'Vertex AI Codestral' },
          { value: 'vertex_ai/gemini-1.5', label: 'Vertex AI Gemini 1.5' },
          { value: 'vertex_ai/gemini-1.5-flash', label: 'Vertex AI Gemini 1.5 Flash' },
          { value: 'vertex_ai/gemini-2.0-flash', label: 'Vertex AI Gemini 2.0 Flash' },
          { value: 'vertex_ai/imagen-3.0', label: 'Vertex AI Imagen 3.0' },
          { value: 'vertex_ai/mistral-large', label: 'Vertex AI Mistral Large' },
          { value: 'vertex_ai/multimodalembedding-001', label: 'Vertex AI Multimodal Embedding' },
          { value: 'vertex_ai/text-embedding-005', label: 'Vertex AI Text Embedding 005' },
          { value: 'vertex_ai/text-multilingual-embedding-002', label: 'Vertex AI Text Multilingual Embedding' },
          { value: 'vertex_ai/gemini-2.5-pro', label: 'Vertex AI Gemini 2.5 Pro' },
          { value: 'vertex_ai/gemini-2.5-flash', label: 'Vertex AI Gemini 2.5 Flash' },
          { value: 'vertex_ai/gemini-2.5-flash-lite', label: 'Vertex AI Gemini 2.5 Flash Lite' },
          { value: 'vertex_ai/claude4-sonnet', label: 'Vertex AI Claude 4 Sonnet' }
        ];
      case 'openai':
        return [
          { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
          { value: 'gpt-4o', label: 'GPT-4o' },
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
        ];
      case 'anthropic':
        return [
          { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
          { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
        ];
      default:
        return [];
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const config = await aiConfigService.loadConfig();
      setSettings(config);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres IA:', error);
      setStatus({
        type: 'error',
        message: 'Erreur lors du chargement des paramètres'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setIsLoading(true);
      
      // Valider la configuration
      const errors = aiConfigService.validateConfig(settings);
      if (errors.length > 0) {
        setStatus({
          type: 'error',
          message: `Erreurs de validation: ${errors.join(', ')}`
        });
        return;
      }
      
      // Sauvegarder via le service
      await aiConfigService.saveConfig(settings);
      
      setStatus({
        type: 'success',
        message: 'Paramètres IA sauvegardés avec succès'
      });
      
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setStatus({
        type: 'error',
        message: 'Erreur lors de la sauvegarde des paramètres'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderChange = (event) => {
    const newProvider = event.target.value;
    const availableModels = getAvailableModels(newProvider);
    
    setSettings(prev => ({
      ...prev,
      provider: newProvider,
      model: availableModels.length > 0 ? availableModels[0].value : '',
      apiKey: newProvider === 'proxy-llm' ? '' : prev.apiKey
    }));
  };

  const handleModelChange = (event) => {
    setSettings(prev => ({
      ...prev,
      model: event.target.value
    }));
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Seuls les Super Administrateurs peuvent accéder aux paramètres IA.
        </Alert>
      </Box>
    );
  }

  const availableModels = getAvailableModels(settings.provider);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SmartToyIcon color="primary" />
        <Typography variant="h5">
          Paramètres IA
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configuration globale pour tous les services d'intelligence artificielle de l'application.
      </Typography>

      {status.message && (
        <Alert severity={status.type} sx={{ mb: 3 }}>
          {status.message}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Stratégie de Planification
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl size="small" fullWidth sx={{ maxWidth: 600 }}>
            <InputLabel>Stratégie de planification des astreintes</InputLabel>
            <Select
              value={settings.planningStrategy || 'multi-agents'}
              label="Stratégie de planification des astreintes"
              onChange={(e) => handleSettingChange('planningStrategy', e.target.value)}
            >
              <MenuItem value="multi-agents">🎯🧠 Hybride (Recommandé) - Déterministe + Manager IA</MenuItem>
              <MenuItem value="deterministic">⚡ Algorithme Déterministe - Rapide et fiable</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {settings.planningStrategy === 'multi-agents' 
              ? "🎯 Mode Hybride : L'algorithme déterministe génère le planning, puis le Manager IA l'analyse et suggère des améliorations pour atteindre l'équité parfaite."
              : "⚡ Mode Déterministe pur : Génération ultra-rapide sans validation IA (idéal pour les tests)."
            }
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Configuration du fournisseur IA (pour le Manager IA)
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {/* Fournisseur IA */}
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Fournisseur IA</InputLabel>
            <Select
              value={settings.provider}
              label="Fournisseur IA"
              onChange={handleProviderChange}
              disabled={settings.planningStrategy === 'deterministic'}
            >
              {providers.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Modèle IA */}
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Modèle IA</InputLabel>
            <Select
              value={settings.model}
              label="Modèle IA"
              onChange={handleModelChange}
              disabled={settings.planningStrategy === 'deterministic'}
            >
              {availableModels.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Clé API (si nécessaire) */}
        {settings.provider !== 'proxy-llm' && (
          <TextField
            fullWidth
            size="small"
            label="Clé API Proxy LLM (optionnelle)"
            value={settings.apiKey}
            onChange={(e) => handleSettingChange('apiKey', e.target.value)}
            placeholder="Laissez vide pour utiliser la clé configurée sur le serveur"
            helperText="Optionnel : clé API spécifique pour ce fournisseur"
            sx={{ mb: 2 }}
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {settings.planningStrategy === 'deterministic'
            ? "⚡ Aucun modèle IA nécessaire en mode Déterministe pur."
            : settings.provider === 'proxy-llm'
            ? "Le Proxy LLM Orange utilise la configuration serveur centralisée."
            : "Configuration directe avec le fournisseur IA sélectionné."
          }
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Paramètres avancés
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="Température"
            type="number"
            value={settings.temperature}
            onChange={(e) => handleSettingChange('temperature', parseFloat(e.target.value))}
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            helperText="Créativité (0 = déterministe, 2 = très créatif)"
            sx={{ minWidth: 150 }}
          />

          <TextField
            size="small"
            label="Tokens maximum"
            type="number"
            value={settings.maxTokens}
            onChange={(e) => handleSettingChange('maxTokens', parseInt(e.target.value))}
            inputProps={{ min: 100, max: 4000 }}
            helperText="Longueur maximale de la réponse"
            sx={{ minWidth: 150 }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={settings.enableMultipleSolutions}
              onChange={(e) => handleSettingChange('enableMultipleSolutions', e.target.checked)}
            />
          }
          label="Générer plusieurs solutions et choisir la meilleure"
        />

        {settings.enableMultipleSolutions && (
          <TextField
            size="small"
            label="Nombre de solutions"
            type="number"
            value={settings.maxSolutions}
            onChange={(e) => handleSettingChange('maxSolutions', parseInt(e.target.value))}
            inputProps={{ min: 2, max: 5 }}
            helperText="Augmente la qualité mais prend plus de temps (3-5 solutions générées)"
            sx={{ ml: 4, mt: 1, minWidth: 150 }}
          />
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Augmente la qualité mais prend plus de temps (3-5 solutions générées)
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveSettings}
          disabled={isLoading}
        >
          Enregistrer la configuration
        </Button>
      </Box>
    </Box>
  );
};

export default AISettings;
